import { useState } from 'react';
import { API_BASE_URL } from './config';
import { IconCheckCircle, IconAlertTriangle, IconMail } from './Icons';

// Where the unsubscribe link in a cart reminder lands.
//
// By the time anyone sees this page the change has already been made -- the
// backend applied it and redirected here (routes/cartRecovery.js). That is
// deliberate: someone who clicks "stop sending me these" has said what they
// want, and making them confirm on a second screen is the kind of friction
// that ends with "report spam" instead.
//
// So this page has two jobs. Say plainly what happened, including what is NOT
// affected -- order confirmations and delivery updates keep coming, and people
// reasonably worry that unsubscribing means going silent on the order they are
// waiting for. And offer one button back, because the other thing that happens
// to unsubscribe links is that they get clicked by mistake, or by a mail
// scanner on the customer's behalf.
//
// No login needed, in either direction: the signed token in the URL is the
// authority, and it only ever names which account's cart reminders to switch.

const styles = {
  wrap: { maxWidth: 520, margin: '0 auto', padding: '48px 20px 72px', textAlign: 'center' },
  icon: { display: 'flex', justifyContent: 'center', marginBottom: 18 },
  title: { fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 12px' },
  body: { color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 10px' },
  panel: {
    textAlign: 'left', border: '1px solid var(--border)', borderRadius: 12,
    padding: '16px 18px', margin: '24px 0', background: 'var(--bg-secondary)',
  },
  panelTitle: { fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '0 0 8px' },
  list: { margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: '0.92rem' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 8 },
  primary: {
    background: 'var(--color-secondary)', color: 'var(--color-text-on-secondary)', border: 'none',
    borderRadius: 10, padding: '12px 24px', fontSize: '0.98rem', fontWeight: 700, cursor: 'pointer',
  },
  secondary: {
    background: 'transparent', color: 'var(--color-secondary-text)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '12px 24px', fontSize: '0.98rem', fontWeight: 600, cursor: 'pointer',
  },
  error: { color: 'var(--color-primary)', fontWeight: 600, marginTop: 12 },
};

export default function CartRemindersOffPage({ token, invalid, onGoHome }) {
  const [state, setState] = useState(invalid ? 'invalid' : 'off');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const turnBackOn = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/cart-recovery/resubscribe?t=${encodeURIComponent(token)}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'That did not work.');
      setState('on');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (state === 'invalid') {
    return (
      <div style={styles.wrap}>
        <div style={styles.icon}><IconAlertTriangle size={44} /></div>
        <h1 style={styles.title}>This link has expired</h1>
        <p style={styles.body}>
          We could not tell which account it belongs to, so nothing has changed. You can turn cart
          reminders off from your email preferences once you are signed in.
        </p>
        <div style={styles.actions}>
          <button type="button" style={styles.primary} onClick={onGoHome}>Go to AcyMart</button>
        </div>
      </div>
    );
  }

  if (state === 'on') {
    return (
      <div style={styles.wrap}>
        <div style={styles.icon}><IconMail size={44} /></div>
        <h1 style={styles.title}>Cart reminders are back on</h1>
        <p style={styles.body}>We will let you know when you leave something in your cart.</p>
        <div style={styles.actions}>
          <button type="button" style={styles.primary} onClick={onGoHome}>Continue shopping</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.icon}><IconCheckCircle size={44} /></div>
      <h1 style={styles.title}>Cart reminders are off</h1>
      <p style={styles.body}>
        We will not email you about items left in your cart again.
      </p>

      <div style={styles.panel}>
        <p style={styles.panelTitle}>You will still receive</p>
        <ul style={styles.list}>
          <li>Order confirmations and receipts</li>
          <li>Delivery updates, including your delivery key</li>
          <li>Returns, refunds and anything you ask us about</li>
        </ul>
      </div>

      <div style={styles.actions}>
        <button type="button" style={styles.secondary} onClick={turnBackOn} disabled={busy}>
          {busy ? 'Turning them back on…' : 'Turn them back on'}
        </button>
        <button type="button" style={styles.primary} onClick={onGoHome}>Continue shopping</button>
      </div>
      {error && <p style={styles.error}>{error}</p>}
    </div>
  );
}
